/**
 * Zustand store for authentication state management
 * Provides centralized auth state with persistence and type safety
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User } from '../types';
import api from '../utils/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  login: (userData: User, token?: string) => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,

      setUser: (user) => {
        set({ 
          user, 
          isAuthenticated: !!user 
        });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      login: (userData, token) => {
        console.log('🔐 [AUTH STORE] User logged in:', userData.email);
        
        // Store token if provided
        if (token) {
          localStorage.setItem('access_token', token);
        }
        
        set({ 
          user: userData, 
          isAuthenticated: true,
          isLoading: false 
        });
      },

      logout: async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        try {
          // Revoke the refresh token server-side (access tokens expire on their own)
          await api.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {});
        } catch {
          // Continue with local logout even if the API call fails
        } finally {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      checkAuthStatus: async () => {
        set({ isLoading: true });
        
        try {
          const token = localStorage.getItem('access_token');
          
          if (!token) {
            set({ 
              user: null, 
              isAuthenticated: false, 
              isLoading: false 
            });
            return;
          }

          const response = await api.get('/auth/status');
          
          if (response.data.isAuthenticated && response.data.user) {
            console.log('🔐 [AUTH STORE] User authenticated:', response.data.user.email);
            set({ 
              user: response.data.user, 
              isAuthenticated: true,
              isLoading: false 
            });
          } else {
            console.log('🔐 [AUTH STORE] Token invalid, clearing session');
            localStorage.removeItem('access_token');
            set({ 
              user: null, 
              isAuthenticated: false,
              isLoading: false 
            });
          }
        } catch (error) {
          console.error('❌ [AUTH STORE] Auth check failed:', error);
          localStorage.removeItem('access_token');
          set({ 
            user: null, 
            isAuthenticated: false,
            isLoading: false 
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('❌ [AUTH STORE] Failed to rehydrate auth state:', error);
        } else {
          console.log('✅ [AUTH STORE] Auth state rehydrated');
          // Check auth status after rehydration
          if (state) {
            state.checkAuthStatus();
          }
        }
      },
    }
  )
);
